import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Mail, CheckCircle2, Sparkles } from "lucide-react";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import ResourcesFooter from "@/components/ResourcesFooter";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [clubName, setClubName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [busy, setBusy] = useState(false);
  // Set once the register API returns { verification_required: true } — we
  // then show the "check your inbox" screen instead of trying to log in.
  const [pendingVerification, setPendingVerification] = useState(null);
  const [resendState, setResendState] = useState({ busy: false, ok: false, err: "" });

  // Fetch active promo so we can show the "Starter is unlimited this summer"
  // note next to the pricing summary. Same public endpoint the landing page
  // uses — auto-hides after the campaign ends.
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/promo/summer-2026`)
      .then((r) => setPromo(r.data))
      .catch(() => {});
  }, []);

  if (user) {
    if (user.role === "MEMBER") return <Navigate to="/me" replace />;
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setEmailExists(false);
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      const result = await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        club_name: clubName.trim(),
        terms_accepted: termsAccepted,
      });
      // New flow (post-Brevo, Feb 2026): register no longer signs the user
      // in. Backend returns { verification_required, email_sent, email }
      // and we show a "check your inbox" screen. If the response still
      // includes a `role` we know it's the legacy (auto-signed-in) shape
      // and fall through to the old navigate behaviour.
      if (result?.verification_required) {
        setPendingVerification(result);
      } else if (result?.role === "MEMBER") {
        navigate("/me");
      } else {
        navigate("/");
      }
    } catch (ex) {
      const status = ex.response?.status;
      const detail = formatApiError(ex.response?.data?.detail) || ex.message;
      if (status === 409 && /email/i.test(detail)) {
        setEmailExists(true);
        setErr(`An account with "${email.trim().toLowerCase()}" already exists.`);
      } else {
        setErr(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-bg min-h-screen flex">
      <div
        className="hidden md:block md:w-1/2 relative overflow-y-auto"
        style={{ background: "#0a0a0a" }}
        data-testid="register-plans-pane"
      >
        <div className="p-12 max-w-lg mx-auto">
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">
            Camera Club Score
          </div>
          <h1 className="text-3xl font-semibold leading-tight text-white mb-3">
            Everything your club needs to score competitions.
          </h1>
          <p className="text-neutral-400 text-sm leading-relaxed mb-6">
            Live scoring, printable results, season league tables, and a
            public portal for your members. Set up in a minute; try it
            free for a month.
          </p>
          <div
            className="inline-flex items-center gap-2 mb-8 px-3 py-2 text-sm"
            style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)" }}
            data-testid="register-trial-badge"
          >
            <CheckCircle2 size={14} className="text-amber-400" />
            <span className="text-neutral-200">
              <strong className="text-white">30-day free trial</strong>
              <span className="muted-text"> &middot; no card required</span>
            </span>
          </div>

          {/* Starter card */}
          <div
            className="p-5 mb-4 relative"
            style={{
              background: "#0f0f0f",
              border: promo?.active ? "1px solid #fbbf24" : "1px solid #2a2a2a",
            }}
            data-testid="register-plan-starter"
          >
            {promo?.active && (
              <div
                className="absolute -top-2.5 left-4 px-2 py-0.5 text-[9px] tracking-widest uppercase font-medium flex items-center gap-1"
                style={{ background: "#fbbf24", color: "#0a0a0a" }}
                data-testid="register-plan-starter-ribbon"
              >
                <Sparkles size={10} /> Launch offer
              </div>
            )}
            <div className="flex items-baseline justify-between mb-2">
              <div className="ccs-label">Starter</div>
              <div>
                <span className="text-2xl font-semibold text-white">£60</span>
                <span className="muted-text text-xs">/year</span>
              </div>
            </div>
            <ul className="text-xs muted-text space-y-1.5">
              {promo?.active ? (
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={12} className="text-amber-400 mt-0.5" />
                  <span>
                    <strong className="text-amber-400">Unlimited photographers</strong>{" "}
                    <span className="mono text-[9px] muted-text">
                      (until {promo.ends_label})
                    </span>
                  </span>
                </li>
              ) : (
                <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5" /> Up to <strong className="text-white">20 photographers</strong></li>
              )}
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5" /> Unlimited seasons &amp; competitions</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5" /> Full PDF / CSV export</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5" /> Public portal sharing</li>
            </ul>
          </div>

          {/* Unlimited card */}
          <div
            className="p-5 mb-6 relative"
            style={{ background: "#0f0f0f", border: "1px solid #fbbf24" }}
            data-testid="register-plan-unlimited"
          >
            <div
              className="absolute -top-2 left-4 px-2 py-0.5 text-[10px] tracking-widest uppercase font-medium"
              style={{ background: "#fbbf24", color: "#0a0a0a" }}
            >
              Most flexible
            </div>
            <div className="flex items-baseline justify-between mb-2 mt-1">
              <div className="ccs-label text-amber-400">Unlimited</div>
              <div>
                <span className="text-2xl font-semibold text-white">£80</span>
                <span className="muted-text text-xs">/year</span>
              </div>
            </div>
            <ul className="text-xs muted-text space-y-1.5">
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-amber-400 mt-0.5" /> <strong className="text-white">Unlimited photographers</strong></li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-amber-400 mt-0.5" /> Unlimited seasons &amp; competitions</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-amber-400 mt-0.5" /> Full PDF / CSV export</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-amber-400 mt-0.5" /> Priority email support</li>
            </ul>
          </div>

          <p className="muted-text text-xs">
            Pick your plan later &mdash; nothing to choose now. You get 30
            days on the full app; if you decide it&apos;s useful, upgrade
            from your billing dashboard.
            <Link to="/#pricing" className="text-amber-400 hover:underline ml-1">
              Full pricing details
            </Link>
          </p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        {pendingVerification ? (
          <VerificationSentPanel
            email={pendingVerification.email}
            emailSent={pendingVerification.email_sent}
            resendState={resendState}
            onResend={async () => {
              setResendState({ busy: true, ok: false, err: "" });
              try {
                await api.post("/auth/resend-verification", { email: pendingVerification.email });
                setResendState({ busy: false, ok: true, err: "" });
              } catch (ex) {
                setResendState({
                  busy: false,
                  ok: false,
                  err: formatApiError(ex.response?.data?.detail) || ex.message,
                });
              }
            }}
          />
        ) : (
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="register-form"
        >
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3 md:hidden">
            Camera Club Score
          </div>
          <h2 className="text-3xl font-semibold mb-2">Create account</h2>
          <p className="muted-text mb-4 text-sm">
            Set up your login so you can record scores and view results.
          </p>
          <div
            className="mb-8 flex items-center gap-2 text-xs md:hidden"
            data-testid="register-trial-badge-mobile"
          >
            <CheckCircle2 size={12} className="text-amber-400" />
            <span className="muted-text">
              <strong className="text-white">30-day free trial</strong> &middot; no card required
            </span>
          </div>
          <div className="mb-8 hidden md:block">
            <p className="muted-text text-xs">
              Free for 30 days &middot; then £60/year Starter or £80/year Unlimited.
              <Link to="/#pricing" className="text-amber-400 hover:underline ml-1">See plans</Link>
            </p>
          </div>

          <div className="form-row">
            <label className="ccs-label" htmlFor="reg-name">Full name</label>
            <input
              id="reg-name"
              type="text"
              className="ccs-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              data-testid="register-name-input"
            />
          </div>

          <div className="form-row">
            <label className="ccs-label" htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              className="ccs-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="register-email-input"
            />
          </div>

          <div className="form-row">
            <label className="ccs-label" htmlFor="reg-password">Password</label>
            <input
              id="reg-password"
              type="password"
              className="ccs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
              data-testid="register-password-input"
            />
            <div className="text-xs muted-text mt-1">At least 6 characters.</div>
          </div>

          <div className="form-row">
            <label className="ccs-label" htmlFor="reg-club">Camera Club name</label>
            <input
              id="reg-club"
              type="text"
              className="ccs-input"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              required
              data-testid="register-club-input"
            />
            <div className="text-xs muted-text mt-1">
              Creates a fresh isolated workspace for your club. Shown on PDF score sheets next to the competition name.
            </div>
          </div>

          {err && (
            <div
              className="text-sm danger-text mb-4 py-2 px-3"
              style={{ background: "rgba(127,29,29,0.2)", border: "1px solid #5b1e1e" }}
              data-testid="register-error"
            >
              {err}
              {emailExists && (
                <div className="mt-2">
                  <Link
                    to="/login"
                    className="text-amber-400 hover:text-amber-300 font-medium"
                    data-testid="register-signin-instead-link"
                  >
                    Sign in instead →
                  </Link>
                </div>
              )}
            </div>
          )}

          <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer" data-testid="register-terms-row">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 accent-amber-400"
              data-testid="register-terms-checkbox"
            />
            <span className="muted-text">
              I have read and agree to the{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener"
                className="text-amber-400 hover:text-amber-300"
                data-testid="register-terms-link"
              >
                Terms &amp; Conditions
              </Link>
              {" "}— including the disclaimer that I&rsquo;m responsible for
              maintaining my own backups.
            </span>
          </label>

          <button
            type="submit"
            disabled={busy || !termsAccepted}
            className="ccs-btn w-full justify-center mt-2"
            data-testid="register-submit-button"
          >
            {busy ? "Creating account…" : "Create account"}
          </button>

          <div className="mt-4 text-sm text-center">
            <span className="muted-text">Already registered? </span>
            <Link
              to="/login"
              className="text-amber-400 hover:text-amber-300 font-medium"
              data-testid="register-back-to-login-link"
            >
              Sign in instead
            </Link>
            <span className="muted-text mx-2">·</span>
            <Link
              to="/forgot-password"
              className="text-amber-400 hover:text-amber-300 font-medium"
              data-testid="register-forgot-password-link"
            >
              Forgotten your password?
            </Link>
          </div>

          <ResourcesFooter />
        </form>
        )}
      </div>
    </div>
  );
}

// Panel shown after successful registration but before the user has clicked
// their verification link. Kept in-file since it's only used here and
// shares the same layout container.
function VerificationSentPanel({ email, emailSent, resendState, onResend }) {
  return (
    <div className="w-full max-w-sm" data-testid="verification-sent-panel">
      <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3 md:hidden">
        Camera Club Score
      </div>
      <div className="flex items-center justify-center w-12 h-12 rounded-full mb-5"
           style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.35)" }}>
        <Mail size={22} className="text-amber-400" />
      </div>
      <h2 className="text-3xl font-semibold mb-2">Check your inbox</h2>
      <p className="muted-text mb-6 text-sm leading-relaxed">
        {emailSent ? "We've sent a verification link to" : "Your account is created, but the verification email couldn't be sent. Try resending below, or contact us if the problem persists."}
        {emailSent && (
          <>
            {" "}
            <span className="text-amber-400 font-medium">{email}</span>.{" "}
            Click the link inside to activate your account, then come back and sign in.
          </>
        )}
      </p>
      <p className="muted-text text-xs mb-6">
        Not showing up? Check your spam folder — DKIM/DMARC signing means it should land in the inbox, but new senders sometimes get held for a minute.
      </p>

      <div className="flex flex-col gap-3">
        <Link
          to="/login"
          className="ccs-btn text-center"
          data-testid="verification-sent-signin-link"
        >
          Go to sign in
        </Link>
        <button
          type="button"
          onClick={onResend}
          disabled={resendState.busy}
          className="ccs-btn-ghost"
          data-testid="verification-sent-resend-button"
        >
          {resendState.busy ? "Resending…" : "Resend verification email"}
        </button>
        {resendState.ok && (
          <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid="verification-sent-resend-ok">
            <CheckCircle2 size={14} /> Verification email sent again — check your inbox.
          </div>
        )}
        {resendState.err && (
          <div className="text-xs danger-text" data-testid="verification-sent-resend-err">
            {resendState.err}
          </div>
        )}
      </div>

      <ResourcesFooter />
    </div>
  );
}
